import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { FrameOption } from '@renderer/types/configState'
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa'

const Frame: React.FC = () => {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { setSessionState, uploadSessionState, logActivity } = useSessionStateStore()
  const { frames: allFrames, frameCategories, config } = useConfigStore()
  const scrollRef = useRef<HTMLDivElement>(null)

  const isCategoryModeEnabled = config?.pages?.frame?.settings?.categoryModeEnabled || false

  // States
  const [currentState, setCurrentState] = useState<'category' | 'frames'>('category')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [selectedFrame, setSelectedFrame] = useState<string | null>(null)
  const [currentFrames, setCurrentFrames] = useState<FrameOption[]>([])

  // Category options for the first state
  const categoryOptions = [
    {
      id: '3cut',
      image: '/assets/yotta/frame-3cut.webp',
      label: '3 Cut'
    },
    {
      id: '4cut1',
      image: '/assets/yotta/frame-4cut1.webp',
      label: '4 Cut Style 1'
    },
    {
      id: '4cut2',
      image: '/assets/yotta/frame-4cut2.webp',
      label: '4 Cut Style 2'
    }
  ]

  // Initialize state based on category mode
  useEffect(() => {
    if (!isCategoryModeEnabled || frameCategories.length === 0) {
      // Skip category selection and go directly to frame selection
      setCurrentState('frames')
      setCurrentFrames(allFrames)
      if (allFrames.length > 0) {
        const firstFrame = allFrames[0]
        setSelectedFrame(firstFrame._id)

        // Update session state with the first frame
        const photoImage = firstFrame.drawParams.images.find((img) => img.type.startsWith('photo-'))
        const photoRatio = photoImage ? photoImage.width / photoImage.height : null

        setSessionState({
          userPreferences: {
            frameId: firstFrame._id,
            framePhotoRatio: photoRatio
          }
        })
      }
    } else {
      setCurrentState('category')
    }
  }, [isCategoryModeEnabled, frameCategories, allFrames])

  // Handle category selection
  const handleCategorySelect = (categoryId: string): void => {
    setSelectedCategory(categoryId)

    // Find frames for this category
    const category = frameCategories.find((cat) => cat.name.toLowerCase().includes(categoryId))

    const framesToShow = category?.frames || allFrames
    setCurrentFrames(framesToShow)

    if (framesToShow.length > 0) {
      const firstFrame = framesToShow[0]
      setSelectedFrame(firstFrame._id)

      // Update session state with the first frame in the category
      const photoImage = firstFrame.drawParams.images.find((img) => img.type.startsWith('photo-'))
      const photoRatio = photoImage ? photoImage.width / photoImage.height : null

      setSessionState({
        userPreferences: {
          frameId: firstFrame._id,
          framePhotoRatio: photoRatio
        }
      })
    }

    logActivity(`Frame category selected: ${categoryId}`)
    setCurrentState('frames')
  }

  // Handle frame selection
  const handleFrameSelect = (frameId: string): void => {
    setSelectedFrame(frameId)

    const frame = allFrames.find((f) => f._id === frameId)
    const photoImage = frame?.drawParams.images.find((img) => img.type.startsWith('photo-'))
    const photoRatio = photoImage ? photoImage.width / photoImage.height : null

    setSessionState({
      userPreferences: {
        frameId: frameId,
        framePhotoRatio: photoRatio
      }
    })

    logActivity(`Frame selected: ${frame?.name || frameId}`)
  }

  // Handle next button
  const handleNext = (): void => {
    if (currentState === 'category' && selectedCategory) {
      handleCategorySelect(selectedCategory)
      return
    }

    if (currentState === 'frames' && selectedFrame) {
      logActivity(`Proceeding to checkout with frame: ${selectedFrame}`)
      uploadSessionState()
      navigate(`/${projectId}/checkout`)
      return
    }
  }

  // Scroll functions
  const scrollLeft = (): void => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -200, behavior: 'smooth' })
    }
  }

  const scrollRight = (): void => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 200, behavior: 'smooth' })
    }
  }

  // Category Selection State
  if (currentState === 'category') {
    return (
      <div className="w-full h-full bg-white flex flex-col">
        {/* Title */}
        <div className="text-center pt-16 pb-8">
          <h1 className="font-title-yotta text-blueGrayYotta text-[96px] font-bold drop-shadow-lg">
            Pilih Frame
          </h1>
        </div>

        {/* Category Options */}
        <div className="flex items-center justify-center px-16">
          <div className="grid grid-cols-3 gap-12 w-full max-w-6xl">
            {categoryOptions.map((option) => (
              <button
                key={option.id}
                onClick={() => setSelectedCategory(option.id)}
                className={`relative group transition-all duration-300 ${
                  selectedCategory === option.id
                    ? 'scale-105 shadow-2xl'
                    : 'hover:scale-102 hover:shadow-xl'
                }`}
              >
                <div
                  className={`relative overflow-hidden rounded-lg border-4 transition-all duration-300 ${
                    selectedCategory === option.id
                      ? 'border-blueGrayYotta'
                      : 'border-gray-200 group-hover:border-gray-300'
                  }`}
                >
                  <img
                    src={option.image}
                    alt={option.label}
                    className="w-full h-[700px] object-cover"
                    crossOrigin="anonymous"
                  />
                  {/* Selection Overlay */}
                  {selectedCategory === option.id && (
                    <div className="absolute inset-0 bg-blueGrayYotta bg-opacity-20 flex items-center justify-center">
                      <div className="bg-white rounded-full p-3">
                        <svg
                          className="w-8 h-8 text-blueGrayYotta"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Next Button */}
        <div className="absolute bottom-12 right-24">
          <button
            onClick={handleNext}
            disabled={!selectedCategory}
            className={`font-body-yotta text-blueGrayYotta text-[32px] hover:opacity-80 transition-opacity active:scale-95 transform ${
              !selectedCategory ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            Next →
          </button>
        </div>
      </div>
    )
  }

  // Frame Selection State
  return (
    <div className="w-full h-full bg-white flex">
      {/* Left Panel - Selected Frame Preview */}
      <div className="w-1/3 flex items-center justify-center p-8">
        {selectedFrame && (
          <div className="w-full max-w-md">
            <img
              src={allFrames.find((f) => f._id === selectedFrame)?.thumbnail || ''}
              alt="Selected Frame"
              className="w-full object-contain drop-shadow-xl"
              crossOrigin="anonymous"
            />
          </div>
        )}
      </div>

      {/* Right Panel - Frame List */}
      <div className="w-2/3 flex flex-col px-8 py-32">
        {/* Title */}
        <div className="mb-8">
          <h1 className="font-title-yotta text-blueGrayYotta text-[128px] font-bold drop-shadow-lg text-left leading-none">
            Frame
          </h1>
        </div>

        {/* Frame List with Scroll */}
        <div className="relative flex-1">
          {/* Left Scroll Button */}
          <button
            onClick={scrollLeft}
            aria-label="Scroll left"
            className="absolute -left-4 top-1/2 transform -translate-y-1/2 z-10 text-blueGrayYotta hover:text-blueGrayYotta/80 transition-colors"
          >
            <FaChevronLeft className="w-8 h-8" />
          </button>

          {/* Right Scroll Button */}
          <button
            onClick={scrollRight}
            aria-label="Scroll right"
            className="absolute -right-4 top-1/2 transform -translate-y-1/2 z-10 text-blueGrayYotta hover:text-blueGrayYotta/80 transition-colors"
          >
            <FaChevronRight className="w-8 h-8" />
          </button>

          {/* Scrollable Frame List */}
          <div
            ref={scrollRef}
            className="flex gap-24 overflow-x-auto scrollbar-hide py-4 px-12"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {currentFrames.map((frame) => (
              <button
                key={frame._id}
                onClick={() => handleFrameSelect(frame._id)}
                className={`relative group flex-shrink-0 transition-all duration-300 ${
                  selectedFrame === frame._id ? 'shadow-2xl' : 'hover:shadow-xl'
                }`}
              >
                <div
                  className={`relative overflow-hidden rounded-lg transition-all duration-300 ${
                    selectedFrame === frame._id
                      ? 'border-blueGrayYotta'
                      : 'border-gray-200 group-hover:border-gray-300'
                  }`}
                  style={{
                    width: selectedCategory === '4cut1' ? '400px' : '200px',
                    height: '600px'
                  }}
                >
                  <div className="w-[400px] h-full">
                    <img
                      src={frame.thumbnail}
                      alt={frame.name}
                      className="w-full h-full object-cover"
                      crossOrigin="anonymous"
                    />
                    {/* Selection Overlay */}
                    {selectedFrame === frame._id && (
                      <div className="absolute inset-0 bg-blueGrayYotta bg-opacity-20 flex items-center justify-center">
                        <div className="bg-white rounded-full p-3">
                          <svg
                            className="w-8 h-8 text-blueGrayYotta"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Next Button */}
        <div className="absolute bottom-12 right-24">
          <button
            onClick={handleNext}
            disabled={!selectedFrame}
            className={`font-body-yotta text-blueGrayYotta text-[32px] hover:opacity-80 transition-opacity active:scale-95 transform ${
              !selectedFrame ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  )
}

export default Frame
